import * as duckdb from '@duckdb/duckdb-wasm';
import { useEffect } from 'react';
import { useAppDispatch } from '../../../app/hooks';
import { DuckDBStatusEnum, setDuckDBStatus } from '../../duckDB/duckDBSlice';
import { useDuckDb } from '../../duckDB/third-party-kit/hooks/useDuckDb';
import { drop } from '../../duckDB/third-party-kit/util/queries';
import { setLastScanDataUpdateTimestamp } from '../scanDataSlice';
import { useFetchScanData } from './useFetchScanData';

const useGetScanDataAndUpdateDuckDB = () => {
  const dispatch = useAppDispatch();
  const { db } = useDuckDb();
  const scanData = useFetchScanData();

  // If a database instance is available, update the table with new data or clear it if data is null.
  useEffect(() => {
    // Function to clear and load scan data into DuckDB
    const clearAndLoadScanData = async (
      db: duckdb.AsyncDuckDB,
      scanData: File | null | undefined,
      tableName: string
    ) => {
      dispatch(setDuckDBStatus(DuckDBStatusEnum.updating));
      const conn = await db.connect();

      await drop(db, tableName);

      if (!scanData) {
        dispatch(setLastScanDataUpdateTimestamp(Date.now()));
        dispatch(setDuckDBStatus(DuckDBStatusEnum.error));
        return;
      }

      await db.registerFileBuffer(
        scanData.name,
        new Uint8Array(await scanData.arrayBuffer())
      );

      await conn.query(
        `CREATE TABLE ${tableName} AS 
        SELECT * FROM read_parquet('${scanData.name}') 
        ORDER BY rowside_id, point_camera_id, point_timestamp;`
      );

      await conn.close();

      dispatch(setDuckDBStatus(DuckDBStatusEnum.dataLoaded));
      dispatch(setLastScanDataUpdateTimestamp(Date.now()));
    };

    // Check if the database is available
    if (db) {
      clearAndLoadScanData(db, scanData, 'scanData');
    }
  }, [db, scanData, scanData?.size, scanData?.lastModified, dispatch]);
};

export default useGetScanDataAndUpdateDuckDB;
